import { IonRouterLink } from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import type { APIContracts } from "authorizenet";
import { DateTime } from "luxon";
import { invoice_status } from "prisma-generated/enums";
import { useState, type ComponentPropsWithoutRef } from "react";
import { apiRequestContractHandler } from "shared/api/apiRequestContractHandler";
import {
  getSocietyProfileAccountingInvoiceEmailContract,
  postSocietyProfileAccountingInvoiceEmailContract,
} from "shared/api/contracts/society/societyId/profiles/profileId/accounting/invoice/invoiceId/email";
import { getSocietyProfileAccountingInvoiceGeneratePdfContract } from "shared/api/contracts/society/societyId/profiles/profileId/accounting/invoice/invoiceId/generatePdf";
import { getSocietyProfileInvoicesContract } from "shared/api/contracts/society/societyId/profiles/profileId/invoices";
import type { SocietyProfileEmailBodyInput } from "shared/api/types/society/[societyId]/profiles/[profileId]/email/[emailHistoryId]";
import Badge from "shared/components/common/Badge";
import EmailPreviewModal from "shared/components/common/email/EmailPreviewModal";
import CloudDownloadIcon from "shared/components/icons/cloud/CloudDownloadIcon";
import MailForwardIcon from "shared/components/icons/mail/MailForwardIcon";
import PillarForm from "shared/components/pillar-form/PillarForm";
import { usePillarModalControl } from "shared/components/pillar-modal/usePillarModalControl";
import PillarTable from "shared/components/pillar-table/PillarTable";
import TemplateComponent from "shared/components/template/TemplateComponent";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { LuxonDateFormats } from "shared/datetime/dateFormats";
import { getInvoiceItemWithEffectiveAndExpirationToShow } from "shared/helpers/invoice/getInvoiceItemWithEffectiveAndExpirationToShow";
import type { InvoiceView } from "shared/mappers/database/accounting/invoice/invoice";
import type { HubOverviewPageRouteParams } from "shared/types/HubPageRouteParamsOverviewPage";
import LoadingPage from "hub/src/components/common/LoadingPage";
import useDeviceInfo from "hub/src/hooks/useDeviceInfo";
import usePDFDownload from "hub/src/hooks/usePDFDownload";
import type { HubRouteConfig } from "hub/src/routes/RouteConfigWrapper";

type UserDetailProps = ComponentPropsWithoutRef<"div"> & {
  routeConfig: HubRouteConfig<HubOverviewPageRouteParams>;
};

const itemsLimit = 3;
export const allInvoicesQueryKey = "all-invoices-ProfileId";
export const unPaidInvoicesQueryKey = "invoices-ProfileId";
export const invoicesTableKey = "invoices";

const defaultTopSectionTemplate = `<style>
  .top-section-template {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    column-gap: calc(1.5 * var(--spacing-base));
    row-gap: calc(1 * var(--spacing-base));

    .button-container {
      display: flex;
      align-items: flex-end;
      flex-wrap: wrap;
      column-gap: calc(1.5 * var(--spacing-base));
      row-gap: calc(1 * var(--spacing-base));
      margin-left: auto;

      > button {
        padding: calc(var(--spacing-base) * 1) calc(var(--spacing-base) * 1.5);
        border: none;
        border-radius: 0.375rem;
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-medium);
        color: hsl(var(--color-neutral-light-hue) var(--color-neutral-light-saturation) var(--color-neutral-light-lightness));
        stroke: hsl(var(--color-neutral-light-hue), var(--color-neutral-light-saturation), var(--color-neutral-light-lightness));
        background-color: hsl(var(--color-society-hue), var(--color-society-saturation), calc(var(--color-society-lightness) - 5%));

        &:hover:not(:disabled) {
          background-color: hsl(var(--color-society-hue) var(--color-society-saturation) var(--color-society-lightness));
        }

        &:disabled {
          opacity: 0.6;
        }
      }
    }
  }
</style>

<div class="top-section-template">
  <div class="button-container">
    {{#routerPush
      type="button"
      route="/edit-user-detail"
      testid="edit-profile-button"
    }}
      Edit Profile
    {{/routerPush}}
  </div>
</div>
`;

const UserDetail = ({ className, routeConfig, ...props }: UserDetailProps) => {
  const device = useDeviceInfo();
  const session = useCustomSession();

  const [selectedInvoice, setSelectedInvoice] = useState<
    InvoiceView | undefined
  >(undefined);
  const modalControl = usePillarModalControl();

  const invoicePdf = usePDFDownload(async (invoiceId, profileId) =>
    apiRequestContractHandler(
      getSocietyProfileAccountingInvoiceGeneratePdfContract,
      {
        params: {
          societyId: session.societyId!,
          profileId: profileId,
          invoiceId: invoiceId,
        },
      },
    ),
  );

  const allInvoicesQuery = useQuery({
    queryKey: [`${allInvoicesQueryKey}-${session.profileId}`],
    queryFn: async () => {
      return await apiRequestContractHandler(
        getSocietyProfileInvoicesContract,
        {
          params: {
            societyId: session.societyId!,
            profileId: session.profileId!,
          },
        },
        {
          status: [
            invoice_status.OPEN,
            invoice_status.PAID,
            invoice_status.PARTIAL_PAID,
          ],
        },
        {
          page: 1,
          pageSize: itemsLimit,
          orderByKey: "total",
          orderType: "desc",
        },
      );
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
  const allInvoicesQueryLoading =
    allInvoicesQuery.isLoading || allInvoicesQuery.isFetching;

  const unPaidInvoicesQuery = useQuery({
    queryKey: [`${unPaidInvoicesQueryKey}-${session.profileId}`],
    queryFn: async () => {
      return await apiRequestContractHandler(
        getSocietyProfileInvoicesContract,
        {
          params: {
            societyId: session.societyId!,
            profileId: session.profileId!,
          },
        },
        {
          status: [invoice_status.OPEN],
        },
        {
          page: 1,
          pageSize: 25,
        },
      );
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
  const unPaidInvoicesQueryLoading =
    unPaidInvoicesQuery.isLoading || unPaidInvoicesQuery.isFetching;

  const handleInvoiceDownload = async (itemId: number) => {
    await invoicePdf.downloadPDF(itemId, session.profileId!);
  };

  const handleInvoiceForwardMail = (item: InvoiceView) => {
    setSelectedInvoice(item);
    modalControl.setIsOpen(true);
  };

  const topSectionTemplate =
    routeConfig?.routeParams?.topSectionTemplate ?? defaultTopSectionTemplate;

  const overviewTemplate = session?.profile?.societyUser
    ? session?.society?.societySettingsPublic?.templates
        ?.userHubOverviewSocietyUser
    : session?.society?.societySettingsPublic?.templates
        ?.userHubOverviewInstitution;

  const templateData = {
    ...session.profile,
    session,
    sessionProfileId: session.profileId,
    allInvoicesQueryData: allInvoicesQuery?.data,
    unPaidInvoicesQueryData: unPaidInvoicesQuery?.data,
    hasUnpaidInvoices: Number(unPaidInvoicesQuery?.data?.results.length) > 0,
  };

  if (allInvoicesQueryLoading || unPaidInvoicesQueryLoading) {
    return <LoadingPage />;
  }

  return (
    <div
      className={`container-page-padding flex flex-col gap-y-2  ${
        className ?? ""
      }`}
      {...props}
    >
      {/* ANCHOR: User Details Template */}
      <div className="flex flex-col">
        {!!topSectionTemplate && (
          <>
            <div className="section-border" />
            <TemplateComponent
              template={topSectionTemplate}
              data={templateData}
            />
          </>
        )}

        {!!session?.profile && !!overviewTemplate && (
          <>
            <div className="section-border" />

            <TemplateComponent
              template={overviewTemplate}
              data={templateData}
            />
          </>
        )}
      </div>

      <div className="section-border" />

      {/* ANCHOR: Financials */}
      {device.isWeb && (
        <>
          <PillarTable<InvoiceView>
            pillarTableContext={{
              // The override replaces the table's own results, so it must
              // never be undefined: while the query is loading or has failed
              // the empty-table row would read `.length` off it and throw.
              tableRowResults: allInvoicesQuery.data?.results ?? [],
            }}
            queryKey={invoicesTableKey}
            className="w-full"
            emptyTableMessage="No Receipts"
          >
            <PillarTable.Column<InvoiceView> heading="Finance">
              {(row) => (
                <>
                  {row.invoiceItems &&
                    row.invoiceItems.map((invoiceItem, index) => {
                      return (
                        invoiceItem.productName +
                        (index === row.invoiceItems!.length - 1 ? "" : ", ")
                      );
                    })}
                </>
              )}
            </PillarTable.Column>
            <PillarTable.Column<InvoiceView> heading="Created">
              {(row) => (
                <>
                  {row?.status === invoice_status.PAID ? (
                    DateTime.fromISO(
                      row?.created?.timestamp ?? row?.created?.timestamp ?? "",
                    ).isValid ? (
                      DateTime.fromISO(
                        row?.created?.timestamp ??
                          row?.modified?.timestamp ??
                          "",
                      ).toFormat(LuxonDateFormats.D)
                    ) : (
                      <></>
                    )
                  ) : (
                    <Badge className="w-fit bg-status-danger-tint !text-neutral-light-100 !text-xs">
                      Payment Due
                    </Badge>
                  )}
                </>
              )}
            </PillarTable.Column>
            <PillarTable.Column<InvoiceView> heading="Payment">
              {(row) => (
                <>
                  {row?.status === invoice_status.PAID ? (
                    DateTime.fromISO(
                      row?.payments?.[0]?.created?.timestamp ??
                        row?.modified?.timestamp ??
                        "",
                    ).isValid ? (
                      DateTime.fromISO(
                        row?.payments?.[0]?.created?.timestamp ??
                          row?.modified?.timestamp ??
                          "",
                      ).toFormat(LuxonDateFormats.D)
                    ) : (
                      <></>
                    )
                  ) : (
                    <Badge className="w-fit bg-status-danger-tint !text-neutral-light-100 !text-xs">
                      Payment Due
                    </Badge>
                  )}
                </>
              )}
            </PillarTable.Column>
            <PillarTable.Column<InvoiceView> heading="Effective">
              {(row) => (
                <>
                  {getInvoiceItemWithEffectiveAndExpirationToShow(
                    row.invoiceItems!,
                  ).effectiveDate
                    ? DateTime.fromISO(
                        getInvoiceItemWithEffectiveAndExpirationToShow(
                          row.invoiceItems!,
                        ).effectiveDate ?? "",
                      ).toFormat(LuxonDateFormats.D)
                    : ""}
                </>
              )}
            </PillarTable.Column>
            <PillarTable.Column<InvoiceView> heading="Expiration">
              {(row) => (
                <>
                  {getInvoiceItemWithEffectiveAndExpirationToShow(
                    row.invoiceItems!,
                  ).expirationDate
                    ? DateTime.fromISO(
                        getInvoiceItemWithEffectiveAndExpirationToShow(
                          row.invoiceItems!,
                        ).expirationDate ?? "",
                      ).toFormat(LuxonDateFormats.D)
                    : ""}
                </>
              )}
            </PillarTable.Column>
            <PillarTable.Column<InvoiceView> heading="">
              {(row) => (
                <p className="mr-2 text-subtle font-bold text-status-success">
                  ${row.total}
                </p>
              )}
            </PillarTable.Column>
            <PillarTable.Column<InvoiceView> heading="">
              {(row) => (
                <p className="text-base-semibold phone:text-end">
                  {`${
                    (
                      row.payments?.[0]
                        ?.additionalInfo as unknown as APIContracts.TransactionResponse
                    )?.accountType ?? ""
                  }
                  ${
                    (
                      row.payments?.[0]
                        ?.additionalInfo as unknown as APIContracts.TransactionResponse
                    )?.accountNumber ?? ""
                  }`}
                </p>
              )}
            </PillarTable.Column>
            <PillarTable.Column<InvoiceView> heading="Actions">
              {(row) => (
                <div className="flex flex-row">
                  <PillarForm.LoadingButton
                    className="button-regular-general-unfilled mr-1.5"
                    onClick={async () => {
                      await handleInvoiceDownload(row.invoiceId!);
                    }}
                  >
                    <CloudDownloadIcon className="h-2.5 w-2.5 stroke-2" />
                  </PillarForm.LoadingButton>
                  <PillarForm.Button
                    className="button-regular-general-filled"
                    onClick={() => {
                      handleInvoiceForwardMail(row);
                    }}
                  >
                    <MailForwardIcon className="h-2.5 w-2.5 stroke-2" />
                  </PillarForm.Button>

                  {row?.status !== invoice_status.PAID && (
                    <IonRouterLink
                      routerLink={`/payment/${row.invoiceId}`}
                      className="button-regular-general-filled hover:text-neutral-light mx-2"
                    >
                      Pay
                    </IonRouterLink>
                  )}
                </div>
              )}
            </PillarTable.Column>
          </PillarTable>

          {(allInvoicesQuery?.data?.totalResults ?? 0) > itemsLimit && (
            <IonRouterLink
              routerLink={"/finance"}
              className="link-decorated ml-2"
            >
              Additional invoices and receipts can be found on the finance page
            </IonRouterLink>
          )}
          <div className="section-border" />
        </>
      )}

      {/* ANCHOR: Modals */}
      <EmailPreviewModal
        control={modalControl}
        emailPreviewCallout={async () =>
          await apiRequestContractHandler(
            getSocietyProfileAccountingInvoiceEmailContract,
            {
              params: {
                societyId: session.societyId!,
                invoiceId: selectedInvoice!.invoiceId!,
                profileId: session.profileId!,
              },
            },
          )
        }
        emailSubmit={async function (
          bodyProps: SocietyProfileEmailBodyInput,
        ): Promise<void> {
          await apiRequestContractHandler(
            postSocietyProfileAccountingInvoiceEmailContract,
            {
              params: {
                societyId: session.societyId!,
                invoiceId: selectedInvoice!.invoiceId!,
                profileId: session.profileId!,
              },
              body: bodyProps,
            },
          );
        }}
        queryKey={`email-preview-invoice-${selectedInvoice?.invoiceId}`}
      />
    </div>
  );
};

export default UserDetail;
